<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Calcular CTS</h1>
    </div>

    <!-- Paso 1: empresa + período -->
    <wz-card v-if="step === 1" title="Paso 1 — Seleccionar empresa y período">
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/3 mb-3 px-2">
          <wz-select-search
            label="Empresa"
            required
            :items="companies"
            :value="form.companyId"
            defaultOption="Buscar empresa por nombre"
            @change="onCompanyChange"
          />
        </div>
        <div class="w-full md:w-1/3 mb-3 px-2">
          <wz-select
            label="Período"
            required
            v-model="form.periodType"
            :items="periodOptions"
            defaultOption="Seleccione un período"
          />
        </div>
        <div class="w-full md:w-1/3 mb-3 px-2">
          <wz-input label="Año" required type="number" min="2000" v-model.number="form.periodYear" />
        </div>
      </div>

      <wz-alert v-if="existingConfirmed" variant="danger" icon="ri-error-warning-line" class="w-full mb-3">
        Este período ya fue confirmado. No se puede recalcular.
      </wz-alert>
      <wz-alert v-else-if="existingDraft" variant="warning" icon="ri-alert-line" class="w-full mb-3">
        Ya existe un cálculo en borrador para este período. Si continúa, se recalculará y se sobreescribirá.
      </wz-alert>

      <div class="flex justify-end gap-2 mt-2">
        <wz-button variant="light" @click="$router.push({ name: 'cts-list' })">Cancelar</wz-button>
        <wz-button variant="primary" class="ml-2" :disabled="!canCalculate" @click="calculate">
          {{ existingDraft ? 'Recalcular' : 'Calcular CTS' }}
        </wz-button>
      </div>
    </wz-card>

    <!-- Paso 2: vista previa -->
    <wz-card v-else title="Paso 2 — Vista previa del cálculo">
      <div class="flex flex-wrap -mx-2 mb-3">
        <div class="w-full md:w-1/4 px-2">
          <small class="text-muted block">Empresa</small>
          <strong>{{ result.companyName }}</strong>
        </div>
        <div class="w-full md:w-1/4 px-2">
          <small class="text-muted block">Período</small>
          <strong>{{ periodLabel(result.periodType) }} {{ result.periodYear }}</strong>
        </div>
        <div class="w-full md:w-1/4 px-2">
          <small class="text-muted block">Colaboradores</small>
          <strong>{{ includedCount }}</strong>
        </div>
        <div class="w-full md:w-1/4 px-2">
          <small class="text-muted block">Total CTS</small>
          <strong><span v-decimal:pen="totals.totalCts"></span></strong>
        </div>
      </div>

      <wz-alert v-if="noAccountCount" variant="warning" icon="ri-error-warning-line" class="w-full mb-3">
        Hay {{ noAccountCount }} colaborador(es) sin cuenta CTS registrada. Complete el dato en la ficha del
        colaborador antes de confirmar.
      </wz-alert>

      <wz-table>
        <thead>
          <tr>
            <th class="text-center">Incluir</th>
            <th>Colaborador</th>
            <th>DNI</th>
            <th>Fecha ingreso</th>
            <th class="text-right">Sueldo</th>
            <th class="text-right">Asig. F</th>
            <th class="text-right">1/6 Grat.</th>
            <th class="text-right">Rem. comp.</th>
            <th class="text-center">Meses</th>
            <th class="text-center">Días</th>
            <th class="text-center">Faltas</th>
            <th class="text-right">CTS</th>
            <th>Cuenta CTS</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="d in result.details" :key="d.id" :class="{ 'row-excluded': d.isExcluded }">
            <td class="text-center">
              <input type="checkbox" :checked="!d.isExcluded" @change="toggleExclude(d)" />
            </td>
            <td>{{ d.employeeFullName }}</td>
            <td>{{ d.employeeDni }}</td>
            <td v-local-date="d.hireDate"></td>
            <td class="text-right"><span v-decimal:pen="d.baseSalary"></span></td>
            <td class="text-right"><span v-decimal:pen="d.familyAllowance"></span></td>
            <td class="text-right"><span v-decimal:pen="d.gratificationSixth"></span></td>
            <td class="text-right"><span v-decimal:pen="d.computableRemuneration"></span></td>
            <td class="text-center">{{ d.completeMonths }}</td>
            <td class="text-center">{{ d.extraDays }}</td>
            <td class="text-center">{{ d.unjustifiedAbsences || 0 }}</td>
            <td class="text-right"><span v-decimal:pen="ctsPayable(d)"></span></td>
            <td>
              <template v-if="d.hasCtsAccount">
                {{ d.ctsBankName }} {{ maskAccount(d.ctsAccount) }}
              </template>
              <span v-else class="text-danger">
                <i class="ri-error-warning-line"></i> Sin cuenta CTS registrada
              </span>
            </td>
          </tr>
          <tr v-if="!result.details || !result.details.length">
            <td colspan="13" class="text-center text-muted">No hay colaboradores elegibles para este período.</td>
          </tr>
        </tbody>
        <tfoot v-if="result.details && result.details.length">
          <tr class="font-bold">
            <td colspan="11" class="text-right">Total CTS (incluidos)</td>
            <td class="text-right"><span v-decimal:pen="totals.totalCts"></span></td>
            <td></td>
          </tr>
        </tfoot>
      </wz-table>

      <div class="flex justify-end gap-2 mt-3">
        <wz-button variant="light" @click="step = 1">Volver</wz-button>
        <wz-button variant="primary" class="ml-2" @click="$router.push({ name: 'cts-list' })">
          Guardar como borrador
        </wz-button>
      </div>
    </wz-card>
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import { CtsService, CompanyService } from '@/core/services'
import { Constants } from '@/core/utils'

export default {
  data() {
    return {
      step: 1,
      companies: [],
      existingPeriods: [],
      form: {
        companyId: null,
        periodType: null,
        periodYear: new Date().getFullYear(),
      },
      result: { details: [] },
    }
  },
  computed: {
    periodOptions() {
      return Object.values(Constants.CtsPeriodType).map(t => ({ key: t.key, label: t.label }))
    },
    existingMatch() {
      if (!this.form.periodType || !this.form.periodYear) return null
      return this.existingPeriods.find(p =>
        p.periodType === this.form.periodType && p.periodYear === Number(this.form.periodYear)) || null
    },
    existingDraft() {
      return this.existingMatch && this.existingMatch.status === Constants.CtsStatus.BORRADOR.key
    },
    existingConfirmed() {
      return this.existingMatch && this.existingMatch.status === Constants.CtsStatus.CONFIRMADO.key
    },
    canCalculate() {
      return this.form.companyId && this.form.periodYear && this.form.periodType && !this.existingConfirmed
    },
    includedDetails() {
      return (this.result.details || []).filter(d => !d.isExcluded)
    },
    includedCount() {
      return this.includedDetails.length
    },
    noAccountCount() {
      return this.includedDetails.filter(d => !d.hasCtsAccount).length
    },
    totals() {
      const totalCts = this.includedDetails.reduce((acc, d) => acc + this.ctsPayable(d), 0)
      return { totalCts }
    },
  },
  methods: {
    periodLabel(type) {
      const t = Constants.CtsPeriodType[type]
      return t ? t.label.split(' —')[0] : type
    },
    ctsPayable(d) {
      return Number(d.ctsAmount || 0) + Number(d.manualAdjustment || 0)
    },
    maskAccount(account) {
      if (!account) return ''
      return `****${String(account).slice(-4)}`
    },
    async onCompanyChange(companyId) {
      this.form.companyId = companyId
      this.existingPeriods = []
      if (!companyId) return
      try {
        const resp = await CtsService.getPeriods(companyId)
        this.existingPeriods = resp.payload || []
      } catch (e) {
        console.error(e)
      }
    },
    async calculate() {
      if (!this.canCalculate) return
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await CtsService.calculate({
          companyId: this.form.companyId,
          periodType: this.form.periodType,
          periodYear: Number(this.form.periodYear),
        })
        this.result = resp.payload || { details: [] }
        this.step = 2
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al calcular la CTS')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async toggleExclude(d) {
      const exclude = !d.isExcluded
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await CtsService.excludeDetail(d.id, exclude, null)
        this.result = resp.payload || this.result
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al actualizar la exclusión')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async loadCompanies() {
      try {
        const resp = await CompanyService.listAll()
        this.companies = (resp.payload || []).map(c => ({ key: c.id, label: c.name }))
      } catch (e) {
        console.error(e)
      }
    },
  },
  async created() {
    await this.loadCompanies()
  },
}
</script>

<style scoped>
.row-excluded {
  opacity: 0.5;
}
</style>
